import axios from "axios";
import cookieManager from "./CookieManager";
import LoginHelper from "./LoginHelper";
import Constants from "./Constants";
import {IS_LOCAL} from "../env";
import SessionHelper from "./Utils/SessionHelper";

class Request {
  constructor() {
    this.maxRetries = 5;
  }

  // Defualt configuration for axios requests
  // This can be overridden by passing a config object to the GET or POST methods
  defaultConfig = {
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-API-Version": "v1",
    },
    validateStatus: (status) => status < 500,
  };
  
  getDefaultConfig() {
    return JSON.parse(JSON.stringify(this.defaultConfig));
  }

  getDefaultSessionDataModel() {
    return JSON.parse(JSON.stringify(SessionHelper.getSessionData));
  }

  setDefaultHeaders(headers) {
    const skillsId = cookieManager.get(Constants.SKILLS_ID);
    const accessToken = cookieManager.get(Constants.ACCESS_TOKEN);
    const afUserId = cookieManager.getAfUserId();

    // Set default header x-session-data
    headers["x-session-data"] = JSON.stringify(
      this.getDefaultSessionDataModel()
    );

    headers["skills_id"] = skillsId || "";
    headers["x-access-token"] = accessToken || "";
    headers["af-user-id-web"] = afUserId || "";

    // LOG
    console.log("Default headers set:", JSON.stringify(headers));
  }

  // Encapsulate get
  async GET(url, config = {}, retries = 0) {
    // Combine default with custom configuration
    const finalConfig = {
      ...this.getDefaultConfig(),
      ...config,
      headers: {
        ...this.getDefaultConfig().headers,
        ...(config.headers || {}),
      },
    };
    this.setDefaultHeaders(finalConfig.headers);

    if (IS_LOCAL)
      console.log(
        `GET request to ${url} with config:`,
        JSON.stringify(finalConfig),
        "retries:",
        retries
      );

    try {
      const request = await axios.get(url, {
        ...finalConfig,
        headers: finalConfig.headers,
      });

      if (IS_LOCAL)
        console.log(`${url} GET response: ${JSON.stringify(request)}`);

      return request.data;
    } catch (error) {
      return this._handleError(
        error,
        () => this.GET(url, config, retries + 1),
        retries
      );
    }
  }

  // Encapsulate post
  async POST(url, data = {}, config = {}, retries = 0) {
    // Combine default with custom configuration
    const finalConfig = {
      ...this.getDefaultConfig(),
      ...config,
      headers: {
        ...this.getDefaultConfig().headers,
        ...(config.headers || {}),
      },
    };
    this.setDefaultHeaders(finalConfig.headers);

    if (IS_LOCAL)
      console.log(
        `POST request to ${url} with data:`,
        JSON.stringify(data),
        "config:",
        JSON.stringify(finalConfig),
        "retries:",
        retries
      );

    try {
      const request = await axios.post(url, data, {
        ...finalConfig,
        headers: finalConfig.headers,
      });

      if (IS_LOCAL)
        console.log(`${url} POST response: ${JSON.stringify(request)}`);

      return request.data;
    } catch (error) {
      return this._handleError(
        error,
        () => this.POST(url, data, config, retries + 1),
        retries
      );
    }
  }

  // Handle accessToken expiration
  async _handleError(error, retryFn, retries) {
    if (retries >= this.maxRetries) {
      console.error(`Max retries.`);
      throw error;
    }

    /* TODO - To do this i should check if the error is a 498, but i cant get the status code from the error
        if (error.response && error.response.status === 498) {
        */
    console.warn(
      "Error 498: accessToken expired. Trying to refresh the token..."
    );
    const refreshToken = cookieManager.get(Constants.REFRESH_TOKEN);
    const skillsID = cookieManager.get(Constants.SKILLS_ID);

    if (refreshToken) {
      try {
        // Refresh token
        await LoginHelper.LoginWithRefreshToken(refreshToken, skillsID);
        return retryFn();
      } catch (refreshError) {
        console.error("Error refreshing the token:", refreshError);
        throw refreshError;
      }
    } else {
      console.error("No refresh token.");
      throw error;
    }

    //}
  }
}

export default new Request();
